import React, { cloneElement, isValidElement, useCallback, useEffect, useRef } from 'react';
import { useReducedMotion } from 'motion/react';
import { glowEffect, ringEffect, type PressEffect } from './effects';
import './Pressable.module.css';

interface PressableProps {
  effect: PressEffect;
  glowColor?: string;
  ringColor?: string;
  children: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
  disabled?: boolean;
}

export function Pressable({
  effect,
  glowColor = 'currentColor',
  ringColor = 'currentColor',
  children,
  disabled = false,
}: PressableProps) {
  const shouldReduce = useReducedMotion() ?? false;
  const cleanupRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nodeRef = useRef<HTMLSpanElement | null>(null);

  // Cleanup on unmount: cancel any pending timer and remove the active node
  useEffect(() => {
    return () => {
      if (cleanupRef.current) clearTimeout(cleanupRef.current);
      if (nodeRef.current) {
        nodeRef.current.remove();
        nodeRef.current = null;
      }
    };
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (typeof children.props.onClick === 'function') {
        children.props.onClick(e);
      }
      if (shouldReduce || disabled || effect === 'none') return;
      const target = e.currentTarget;

      // Remove any prior animating node before appending a new one
      if (nodeRef.current) {
        nodeRef.current.remove();
        nodeRef.current = null;
      }
      if (cleanupRef.current) {
        clearTimeout(cleanupRef.current);
        cleanupRef.current = null;
      }

      let node: HTMLSpanElement;
      let durationMs: number;
      if (effect === 'glow') {
        const rect = target.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;
        const diameter = Math.max(rect.width, rect.height) * 2;
        node = glowEffect.createNode(glowColor, clickX, clickY, diameter);
        durationMs = glowEffect.durationMs;
      } else {
        node = ringEffect.createNode(ringColor);
        durationMs = ringEffect.durationMs;
      }
      target.appendChild(node);
      nodeRef.current = node;

      cleanupRef.current = setTimeout(() => {
        node.remove();
        if (nodeRef.current === node) nodeRef.current = null;
        cleanupRef.current = null;
      }, durationMs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [children.props.onClick, shouldReduce, disabled, effect, glowColor, ringColor],
  );

  if (!isValidElement(children)) return children;

  // The glow/ring nodes are appended as `position: absolute` children — they
  // need a non-`static` positioning context on the button to anchor to.
  // We default to `relative`, but if the consumer already opted into a
  // non-static position (e.g. an overlaid close button using `absolute`),
  // their choice wins. Clobbering the consumer's `position: absolute` was
  // the cause of the SettingsShell close-button-centered-instead-of-top-right
  // bug.
  const childPosition = children.props.style?.position;
  const needsRelative = !childPosition || childPosition === 'static';
  return cloneElement(children, {
    onClick: handleClick,
    style: {
      ...children.props.style,
      ...(needsRelative ? { position: 'relative' as const } : {}),
    },
  } as React.HTMLAttributes<HTMLElement>);
}
